import React, { useEffect, useMemo, useState } from 'react';
import { useRememberedPeriod } from './rememberedPeriod';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { apiFetch } from '../apiClient';
import { projectPath } from './projectDeepLink';
import { useGridSort, sortRows, lower } from './Configuration';
import { BILLING_CATEGORIES, DEFAULT_BILLING_CATEGORY_FILTER, UNCATEGORIZED } from '../billingCategories';
import {
  MILESTONE_STATUSES, amountOf, burndownSeries, categoryOf, countyLabel, daysUntil, isDone, milestoneBucket,
  shortProjectName,
  categoryTotals, collapseRecurring, compactPeriodLabel, contractCertainty, expandDrawdowns, expandRecurring, maSummary, matchesCardFilter, monthAttainment, monthYearLabel, noBillingRuns, slipDays, valueTypeOf, periodDateOf, periodKeyOf, periodLabelOf, pivot, rowState, targetSortKey, todayYmd, trackOf,
  yearOf, yearTotals, ymd,
} from './milestoneTrackerLogic';

// Portfolio-wide milestone tracker (item 7, Jeremy 2026-09-09; reworked at Judy's request
// 2026-09-16 to replace the Power BI "Forecasted Values" report). What's due when and what it's
// worth, across every project -- distinct from Project Calendar, which is an agenda across several
// event types rather than a financial view of milestones specifically.

const STATUS_BADGE_CLASS = {
  'Not Started': 'badge-blue',
  'In Progress': 'badge-blue',
  Delivered: 'badge-green',
  Accepted: 'badge-green',
  Billed: 'badge-green',
  Paid: 'badge-green',
};

// Tint comes from .mt-row.is-* in App.css -- see the note there.
const rowClass = (state) => `mt-row${state ? ` is-${state}` : ''}`;

function money(n) {
  return n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
// Compact form for grid cells, where a full $1,575,768 in twelve columns is unreadable.
function moneyShort(n) {
  const v = Number(n || 0);
  if (v === 0) return '';
  if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(2)}M`;
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${Math.round(v)}`;
}
function pct(part, whole) {
  if (!whole) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}
function plural(n) {
  return `${n} milestone${n === 1 ? '' : 's'}`;
}
// Everything interpolated into the email HTML goes through this -- milestone titles really do
// contain ampersands ("R&D Delivery 2"), and a title is free text, not a safe literal.
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
// Prints one block on its own (Judy wants the cards and the charts mailable separately). The CSS
// that does the hiding lives in App.css under body.print-one; this just marks the target and
// cleans up afterwards. afterprint doesn't fire in every browser when the dialog is dismissed, so
// a timeout backstops it -- leaving .print-one on <body> would blank the live page.
function printSection(id) {
  const el = id ? document.getElementById(id) : null;
  if (id && !el) { window.print(); return; }
  const cleanup = () => {
    document.body.classList.remove('print-one');
    if (el) el.classList.remove('print-target');
    window.removeEventListener('afterprint', cleanup);
  };
  if (el) {
    el.classList.add('print-target');
    document.body.classList.add('print-one');
  }
  window.addEventListener('afterprint', cleanup);
  window.print();
  setTimeout(cleanup, 1000);
}

function safeFileSegment(value) {
  return String(value || '').replace(/[\\/:*?"<>|]/g, '-').slice(0, 60);
}

// Full date on hover, month+year on screen (Judy, 2026-09-16) -- the day isn't hidden, just off the
// grid, and the Excel export still carries it.
function DateCell({ value }) {
  const full = ymd(value);
  return <span title={full || undefined}>{monthYearLabel(value)}</span>;
}

function milestoneColumns(today) {
  return [
    {
      key: 'state', label: 'State', value: (m) => lower(m.project?.account?.state),
      render: (m) => m.project?.account?.state || '—',
      excel: (m) => m.project?.account?.state || '',
    },
    {
      // Account name minus the redundant " County" -- full value on hover and in the export.
      key: 'county', label: 'County', value: (m) => lower(countyLabel(m.project)),
      render: (m) => <span title={m.project?.account?.name || undefined}>{countyLabel(m.project) || '—'}</span>,
      excel: (m) => m.project?.account?.name || '',
    },
    {
      // The account name used to print again on a second line under every project -- dropped, it's
      // the County column now.
      key: 'project', label: 'Project', value: (m) => lower(shortProjectName(m.project)),
      render: (m) => <Link to={projectPath('/schedule', m.projectId)}>{shortProjectName(m.project) || '—'}</Link>,
      excel: (m) => shortProjectName(m.project),
    },
    {
      key: 'pm', label: 'Project Manager', value: (m) => lower(m.project?.projectManager?.name),
      render: (m) => m.project?.projectManager?.name || '—',
      excel: (m) => m.project?.projectManager?.name || '',
    },
    {
      // FCST/ACT as its own sortable column (Judy, 2026-09-16) -- derived from status, so sorting on
      // it groups every completed milestone together regardless of which "done" status it carries.
      key: 'track', label: 'FCST/ACT', value: (m) => trackOf(m),
      render: (m) => (
        <span className={`badge ${trackOf(m) === 'ACT' ? 'badge-green' : 'badge-blue'}`} style={{ fontSize: 10 }}>
          {trackOf(m)}
        </span>
      ),
      excel: (m) => trackOf(m),
    },
    { key: 'sequence', label: 'Seq', value: (m) => m.sequence ?? -1, render: (m) => m.sequence ?? '—', excel: (m) => m.sequence ?? '' },
    { key: 'sowMilestoneNumber', label: '#', value: (m) => m.sowMilestoneNumber || '', render: (m) => m.sowMilestoneNumber || '—', excel: (m) => m.sowMilestoneNumber || '' },
    { key: 'dasNumber', label: 'DAS #', value: (m) => m.dasNumber || '', render: (m) => m.dasNumber || '—', excel: (m) => m.dasNumber || '' },
    {
      key: 'title', label: 'Milestone', value: (m) => lower(m.title),
      render: (m) => (
        <>
          {m.title}
          {m.isGoLive && <span className="badge badge-green" style={{ marginLeft: 6, fontSize: 9 }} title="Go-live milestone">Go-Live</span>}
        </>
      ),
      excel: (m) => m.title,
    },
    {
      key: 'billingCategory', label: 'Category', value: (m) => m.billingCategory || 'zz',
      render: (m) => (m.billingCategory
        ? <span className="badge badge-blue" style={{ fontSize: 10 }}>{m.billingCategory}</span>
        : <span style={{ color: 'var(--text-mid)' }}>—</span>),
      excel: (m) => m.billingCategory || '',
    },
    { key: 'amount', label: 'Amount', value: (m) => Number(m.amount ?? -1), render: (m) => money(m.amount), excel: (m) => Number(m.amount ?? 0), numeric: true },
    {
      key: 'status', label: 'Status', value: (m) => lower(m.status),
      render: (m) => <span className={`badge ${STATUS_BADGE_CLASS[m.status] || 'badge-blue'}`} style={{ fontSize: 10 }}>{m.status || '—'}</span>,
      excel: (m) => m.status || '',
    },
    { key: 'baselineDate', label: 'Baseline', value: (m) => ymd(m.baselineDate), render: (m) => <DateCell value={m.baselineDate} />, excel: (m) => ymd(m.baselineDate) },
    {
      key: 'targetDate', label: 'Target', value: (m) => targetSortKey(m, today),
      headerTitle: 'Sorts by urgency: overdue first, then due in 30 / 60 / 90 days',
      render: (m) => {
        const d = daysUntil(m.targetDate, today);
        const overdue = d != null && d < 0 && !isDone(m);
        return (
          <span style={{ color: overdue ? 'var(--danger-text)' : undefined, fontWeight: overdue ? 600 : undefined }}>
            <DateCell value={m.targetDate} />
            {overdue && ` (${Math.abs(d)}d late)`}
          </span>
        );
      },
      excel: (m) => ymd(m.targetDate),
    },
    {
      // Days off the baseline. Sorts worst-first when descending, with unbaselined rows last either
      // way -- there's nothing to rank them by.
      key: 'slip', label: 'Slip', numeric: true,
      headerTitle: 'Days between the baseline date and where the milestone landed — its actual date '
        + 'if it is done, its current target date if it is not. Positive is late, negative is early. '
        + 'Blank means no baseline was set, so there is nothing to measure against.',
      value: (m) => (slipDays(m) ?? -99999),
      render: (m) => {
        const d = slipDays(m);
        if (d == null) return <span style={{ color: 'var(--text-mid)' }}>—</span>;
        if (d === 0) return <span style={{ color: 'var(--text-mid)' }}>on time</span>;
        const late = d > 0;
        return (
          <span
            style={{ color: late ? 'var(--danger-text)' : 'var(--green)', fontWeight: 600 }}
            title={late ? `${d} days later than baseline` : `${Math.abs(d)} days earlier than baseline`}
          >
            {late ? '+' : '−'}{Math.abs(d)}d
          </span>
        );
      },
      excel: (m) => slipDays(m) ?? '',
    },
    { key: 'actualDate', label: 'Actual', value: (m) => ymd(m.actualDate), render: (m) => <DateCell value={m.actualDate} />, excel: (m) => ymd(m.actualDate) },
    {
      // Slip says how far off plan a date is; this says how restless it has been getting there,
      // which is a different finding -- a milestone that moved five times to land on plan is not
      // the same as one that never moved (ADR 0047).
      key: 'moveCount', label: 'Moves', numeric: true,
      headerTitle: 'How many times this milestone’s target or actual date has been edited by hand. '
        + 'Edits made before this log existed are not counted.',
      value: (m) => (m._count?.dateChanges ?? 0),
      render: (m) => {
        const n = m._count?.dateChanges ?? 0;
        // See ProjectLaunch's MilestoneMovesCell: a baseline that no longer matches the target is
        // proof of movement that predates the log, and a blank there would read as "never moved".
        const before = !n && !!m.baselineDate && !!m.targetDate && ymd(m.baselineDate) !== ymd(m.targetDate);
        if (!n && !before) return <span style={{ color: 'var(--text-mid)' }}>—</span>;
        return (
          <span
            className={n > 2 ? 'badge badge-yellow' : 'badge badge-blue'} style={{ fontSize: 10 }}
            title={before
              ? 'Moved before move logging existed — those edits were never recorded, so the count is unknown.'
              : `${n} edit(s) to the target or actual date`}
          >{n}{before ? '+' : '×'}</span>
        );
      },
      excel: (m) => (m._count?.dateChanges ?? 0),
    },
    { key: 'dateSubmitted', label: 'Submitted', value: (m) => ymd(m.dateSubmitted), render: (m) => <DateCell value={m.dateSubmitted} />, excel: (m) => ymd(m.dateSubmitted) },
    { key: 'dateSigned', label: 'Signed', value: (m) => ymd(m.dateSigned), render: (m) => <DateCell value={m.dateSigned} />, excel: (m) => ymd(m.dateSigned) },
    { key: 'dateInvoiced', label: 'Invoiced', value: (m) => ymd(m.dateInvoiced), render: (m) => <DateCell value={m.dateInvoiced} />, excel: (m) => ymd(m.dateInvoiced) },
    { key: 'datePaid', label: 'Paid', value: (m) => ymd(m.datePaid), render: (m) => <DateCell value={m.datePaid} />, excel: (m) => ymd(m.datePaid) },
    { key: 'acceptanceCriteria', label: 'Acceptance Criteria', value: (m) => lower(m.acceptanceCriteria), render: (m) => m.acceptanceCriteria || '—', excel: (m) => m.acceptanceCriteria || '' },
    {
      key: 'valueType', label: 'Value', value: (m) => valueTypeOf(m),
      render: (m) => {
        const type = valueTypeOf(m);
        return type === 'Committed'
          ? <span style={{ color: 'var(--text-mid)' }}>Committed</span>
          : <span className="badge badge-yellow" style={{ fontSize: 10 }} title="Only bills if the customer uses or authorizes it">{type}</span>;
      },
      excel: (m) => valueTypeOf(m),
    },
    { key: 'invoiceNumber', label: 'Invoice #', value: (m) => m.invoiceNumber || '', render: (m) => m.invoiceNumber || '—', excel: (m) => m.invoiceNumber || '' },
  ];
}
const ALL_COLUMN_KEYS = milestoneColumns('2000-01-01').map((c) => c.key);
// Hidden until asked for (Judy, 2026-09-16) -- the billing-admin dates and the prose column are
// what make the grid too wide to scan. Everything stays one click away in the Columns menu.
const DEFAULT_HIDDEN = ['dateSubmitted', 'acceptanceCriteria', 'baselineDate', 'pm'];
// Per-project columns: meaningless once one project is picked, so hidden and locked then.
const PROJECT_COLS = ['project', 'state', 'county', 'pm'];

// Left to right (Judy, 2026-09-16): what's done, what it's worth in total, what's left, then the
// aging buckets getting more urgent rightwards, ending on Overdue. `scope` and `remaining` are
// computed rather than bucketed -- see cardTotals.
const CARDS = [
  { key: 'scope', label: 'Total contracted', accent: 'slate', filterable: false },
  { key: 'complete', label: 'Complete', accent: 'green', value: 'good' },
  { key: 'remaining', label: 'Remaining', accent: 'blue' },
  { key: 'due30', label: 'Due ≤30 Days', accent: 'light-blue' },
  { key: 'due60', label: 'Due 31–60 Days', accent: 'yellow' },
  { key: 'due90', label: 'Due 61–90 Days', accent: 'orange' },
  { key: 'overdue', label: 'Overdue', accent: 'red', value: 'bad' },
];

function BillingCategoryFilter({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const options = [...BILLING_CATEGORIES, UNCATEGORIZED];
  const summary = selected.length === 0 ? 'No categories'
    : selected.length === options.length ? 'All billing'
    : selected.join(', ');
  function toggle(value) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="mt-ctl" onClick={() => setOpen((v) => !v)}
        style={{ minWidth: 140, maxWidth: 210, justifyContent: 'space-between', gap: 6 }}
        title="Which billing categories to include">
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span> ▾
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div className="mt-pop" style={{ left: 0, right: 'auto', minWidth: 180 }}>
            {options.map((o) => (
              <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                {o}
              </label>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
              <button type="button" className="mt-ctl" style={{ height: 24, fontSize: 11 }}
                onClick={() => onChange(DEFAULT_BILLING_CATEGORY_FILTER)}>Reset</button>
              <button type="button" className="mt-ctl" style={{ height: 24, fontSize: 11 }}
                onClick={() => onChange(options)}>All</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Only the widths a user has actually dragged are stored; everything else stays auto-sized, so the
// grid still fits itself to the data out of the box. Persisted per browser -- a column width is a
// personal preference, not something worth a round trip or a schema field.
const WIDTHS_KEY = 'milestoneTracker.columnWidths';

function useColumnWidths() {
  const [widths, setWidths] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(WIDTHS_KEY)) || {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(widths)); } catch { /* private mode -- widths just won't persist */ }
  }, [widths]);
  return [widths, setWidths];
}

function MilestoneColumnHeader({ col, sort, onSort, draggedKey, onDragStart, onDrop, width, onResize }) {
  const active = sort.key === col.key;
  const [resizing, setResizing] = useState(false);

  // Pointer events rather than mouse, so a drag that leaves the header (or the window) still
  // tracks -- setPointerCapture keeps the moves coming to this element until release.
  function startResize(e) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = e.currentTarget.parentElement.getBoundingClientRect().width;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    setResizing(true);
    const move = (ev) => onResize(col.key, Math.max(48, Math.round(startWidth + ev.clientX - startX)));
    const up = () => {
      setResizing(false);
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  }

  return (
    <th
      className="sortable"
      draggable={!resizing}
      onDragStart={() => onDragStart(col.key)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onDrop(col.key); }}
      onClick={() => onSort(col.key)}
      title={col.headerTitle || 'Click to sort, drag the header to reorder, drag the right edge to resize'}
      style={{
        padding: '4px 8px', cursor: 'grab', opacity: draggedKey === col.key ? 0.4 : 1,
        position: 'sticky', top: 0, zIndex: 1, background: 'var(--light-bg)', whiteSpace: 'nowrap',
        textAlign: col.numeric ? 'right' : 'left',
        width, minWidth: width, maxWidth: width,
      }}
    >
      {col.label}
      <span className={`sort-arrow${active ? '' : ' is-idle'}`}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
      <span
        className={`mt-resize${resizing ? ' is-active' : ''}`}
        onPointerDown={startResize}
        onClick={(e) => e.stopPropagation()}
        onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
        role="presentation"
      />
    </th>
  );
}

// Part-to-whole in one bar: what's been delivered against the contracted total. Both segments are
// directly labeled, so it reads without the legend a color-only split would need, and the POC%
// sits on the boundary itself (Judy, 2026-09-16) rather than only in the label above.
function ProgressBar({ complete, scope }) {
  const donePct = scope ? (complete / scope) * 100 : 0;
  // Pinned inside the bar once it's far enough along to fit, outside while the bar is still short.
  const labelInside = donePct >= 18;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-mid)', marginBottom: 4 }}>
        <span><strong style={{ color: 'var(--green)' }}>{money(complete)}</strong> delivered ({pct(complete, scope)})</span>
        <span><strong style={{ color: 'var(--heading)' }}>{money(scope - complete)}</strong> remaining ({pct(scope - complete, scope)})</span>
      </div>
      <div style={{ position: 'relative' }}>
        <div
          style={{ display: 'flex', height: 18, borderRadius: 9, overflow: 'hidden', background: 'var(--light-bg)', gap: 2 }}
          role="img"
          aria-label={`${pct(complete, scope)} of ${money(scope)} delivered`}
        >
          <div style={{ width: `${donePct}%`, background: 'var(--green)' }} />
          <div style={{ flex: 1, background: 'var(--border)' }} />
        </div>
        {scope > 0 && (
          <div
            title="Percent of contract value delivered"
            style={{
              position: 'absolute', top: 0, left: `${Math.min(donePct, 100)}%`, height: 18,
              display: 'flex', alignItems: 'center',
              transform: labelInside ? 'translateX(-100%)' : 'none',
              paddingLeft: labelInside ? 0 : 6, paddingRight: labelInside ? 8 : 0,
              fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
              color: labelInside ? 'var(--white)' : 'var(--heading)',
            }}
          >
            {pct(complete, scope)} POC
          </div>
        )}
      </div>
    </div>
  );
}

// Burn-down of outstanding contract value. Planned comes from target dates, actual from completed
// milestones and stops at today -- the gap between the two lines is the slippage. One y-scale, two
// series, legend plus direct end-labels so the lines aren't told apart by color alone.
function Burndown({ series, grain }) {
  const { points, scope } = series;
  if (points.length < 2 || !scope) {
    return <div style={{ fontSize: 12, color: 'var(--text-mid)', padding: '20px 0', textAlign: 'center' }}>Not enough dated milestones to chart a burn-down.</div>;
  }
  const W = 720;
  const H = 170;
  const pad = { top: 12, right: 12, bottom: 22, left: 48 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;
  const x = (i) => pad.left + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (v) => pad.top + plotH - (v / scope) * plotH;
  const line = (pick) => points
    .map((p, i) => [x(i), pick(p)])
    .filter(([, v]) => v != null)
    .map(([px, v], i) => `${i === 0 ? 'M' : 'L'}${px.toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');

  const gaps = noBillingRuns(points, grain === 'quarter' ? 2 : 3);
  const actualPoints = points.filter((p) => p.actual != null);
  const lastActual = actualPoints[actualPoints.length - 1];
  const lastActualIdx = points.findIndex((p) => p === lastActual);
  // Label density from the space available rather than a fixed count: the compact "Q2 '23" form is
  // about 30px at this size, so work out how many fit and thin them only if they still won't.
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.floor(plotW / 34)));

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-mid)', marginBottom: 2 }}>
        <span><span style={{ display: 'inline-block', width: 18, height: 2, background: 'var(--text-mid)', verticalAlign: 'middle', marginRight: 5 }} />Planned remaining</span>
        <span><span style={{ display: 'inline-block', width: 18, height: 2, background: 'var(--green)', verticalAlign: 'middle', marginRight: 5 }} />Actual remaining</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Burn-down of outstanding contract value">
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={pad.left} x2={W - pad.right} y1={y(scope * f)} y2={y(scope * f)} stroke="var(--border)" strokeWidth="1" />
            <text x={pad.left - 6} y={y(scope * f) + 3} textAnchor="end" fontSize="9" fill="var(--text-mid)">{moneyShort(scope * f) || '$0'}</text>
          </g>
        ))}
        {/* Stretches where nothing was billed at all, called out behind the lines so a long flat
            run reads as a finding rather than as a line that happens not to move. */}
        {gaps.map((run) => {
          const x0 = x(run.startIndex) - plotW / (points.length * 2);
          const x1 = x(run.endIndex) + plotW / (points.length * 2);
          return (
            <g key={`gap-${run.startIndex}`}>
              <rect x={Math.max(pad.left, x0)} y={pad.top} width={Math.min(W - pad.right, x1) - Math.max(pad.left, x0)} height={plotH} fill="var(--row-overdue)" opacity="0.75" />
              <text x={(Math.max(pad.left, x0) + Math.min(W - pad.right, x1)) / 2} y={pad.top + 11} textAnchor="middle" fontSize="9" fontWeight="600" fill="var(--danger-text)">
                {run.length} {grain === 'quarter' ? 'qtrs' : 'mos'} no billing
              </text>
            </g>
          );
        })}
        <path d={line((p) => p.planned)} fill="none" stroke="var(--text-mid)" strokeWidth="2" strokeDasharray="4 3" strokeLinecap="round" />
        <path d={line((p) => p.actual)} fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" />
        {/* A dot per elapsed period. Filled where something was billed, hollow where nothing was --
            so the rhythm of the delivery is visible along the line, not just its slope. */}
        {points.map((p, i) => (
          p.actual == null ? null : (
            <circle
              key={`dot-${p.key}`}
              cx={x(i)} cy={y(p.actual)} r={p.delivered > 0 ? 4 : 3}
              fill={p.delivered > 0 ? 'var(--green)' : 'var(--surface)'}
              stroke={p.delivered > 0 ? 'var(--surface)' : 'var(--text-mid)'}
              strokeWidth={p.delivered > 0 ? 1.5 : 1.5}
            />
          )
        ))}
        {lastActual && (
          <text x={x(lastActualIdx) - 8} y={y(lastActual.actual) - 9} textAnchor="end" fontSize="10" fontWeight="600" fill="var(--text-dark)">
            {moneyShort(lastActual.actual)} left
          </text>
        )}
        {points.map((p, i) => (
          i % labelEvery === 0 ? (
            <text key={p.key} x={x(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="var(--text-mid)">
              {compactPeriodLabel(p.key, grain)}
            </text>
          ) : null
        ))}
        {/* Hover targets are the full column height, not the 4px mark. */}
        {points.map((p, i) => (
          <rect key={`hit-${p.key}`} x={x(i) - plotW / (points.length * 2)} y={pad.top} width={Math.max(6, plotW / points.length)} height={plotH} fill="transparent">
            <title>{[
              periodLabelOf(p.key, grain),
              p.delivered > 0
                ? `Billed this period: ${money(p.delivered)}`
                : (p.isFuture ? 'Not reached yet' : 'Nothing billed this period'),
              p.due > 0 ? `Scheduled this period: ${money(p.due)}` : null,
              `Planned remaining: ${money(p.planned)}`,
              p.actual != null ? `Actual remaining: ${money(p.actual)}` : null,
              ...p.deliveredTitles.slice(0, 5).map((title) => `  • ${title}`),
              p.deliveredTitles.length > 5 ? `  • +${p.deliveredTitles.length - 5} more` : null,
            ].filter(Boolean).join('\n')}</title>
          </rect>
        ))}
      </svg>
    </div>
  );
}



// Fixed hue per category, never cycled, and deliberately none of green/yellow/red -- those mean
// status everywhere else on this page. Every slice is also named and valued in the legend beside
// it, so the color is a shortcut rather than the only way to read the chart.
const CATEGORY_COLOR = {
  PS: 'var(--accent-blue)',
  'R&D': 'var(--accent-violet)',
  License: 'var(--accent-light-blue)',
  Hosting: 'var(--accent-slate)',
  MA: 'var(--accent-orange)',
};
const CATEGORY_FALLBACK = 'var(--border)';
// Delivery work, as against license/hosting/maintenance -- rolled into one "All services" card.
const SERVICE_CATEGORIES = ['PS', 'R&D'];

// Donut rather than a full pie: the hole carries the total, which is the number people actually
// want next to the split. Slices are separated by a 2px stroke in the surface color so adjacent
// hues never touch.
function CategoryPie({ rows, total }) {
  // Rendered at 176px to fit the band's width budget, but drawn on a 210-unit canvas so the slice
  // labels have somewhere to live without the pie taking more width (Judy, 2026-09-17).
  const size = 176;
  const vb = 210;
  const r = 70;
  const inner = 44;
  const label = 80;
  const cx = vb / 2;
  const cy = vb / 2;
  if (!total) return null;
  let angle = -Math.PI / 2;
  const arcs = rows.filter((row) => row.amount > 0).map((row) => {
    const sweep = (row.amount / total) * Math.PI * 2;
    const [a0, a1] = [angle, angle + sweep];
    angle = a1;
    const pt = (rad, radius) => `${(cx + radius * Math.cos(rad)).toFixed(2)},${(cy + radius * Math.sin(rad)).toFixed(2)}`;
    const large = sweep > Math.PI ? 1 : 0;
    const mid = (a0 + a1) / 2;
    // A single category owning the whole contract can't be drawn as an arc (start meets end), so
    // it renders as a ring instead -- which is the honest picture of "100% of it is this".
    const d = sweep >= Math.PI * 2 - 1e-6
      ? `M ${cx - r},${cy} a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 ${-r * 2},0 M ${cx - inner},${cy} a ${inner},${inner} 0 1,1 ${inner * 2},0 a ${inner},${inner} 0 1,1 ${-inner * 2},0`
      : `M ${pt(a0, r)} A ${r},${r} 0 ${large},1 ${pt(a1, r)} L ${pt(a1, inner)} A ${inner},${inner} 0 ${large},0 ${pt(a0, inner)} Z`;
    // Labeled outside the ring in text colors rather than on the slice: white on the lighter hues
    // (orange, light blue) doesn't carry enough contrast to be read. A sliver too thin to label
    // keeps its tooltip and its card instead of crowding its neighbor's label.
    const share = row.amount / total;
    const pctText = ` ${Math.round(share * 100)}%`;
    // Nudged back inside the canvas when a long name at the 3 or 9 o'clock edge would run off it
    // ("License 8%" does). Estimated from the character count rather than measured -- getBBox needs
    // a laid-out DOM, and being a pixel or two out just shifts the label slightly.
    const halfWidth = (row.category.length * 7.4 + pctText.length * 6) / 2;
    const rawX = cx + label * Math.cos(mid);
    return {
      row,
      d,
      share,
      pctText,
      lx: Math.min(Math.max(rawX, halfWidth + 2), vb - halfWidth - 2),
      ly: cy + label * Math.sin(mid),
    };
  });
  return (
    <svg viewBox={`0 0 ${vb} ${vb}`} width={size} height={size} role="img"
      aria-label={`Contract value by billing category, total ${money(total)}`}>
      {arcs.map(({ row, d }) => (
        <path key={row.category} d={d} fill={CATEGORY_COLOR[row.category] || CATEGORY_FALLBACK}
          stroke="var(--surface)" strokeWidth="2" fillRule="evenodd">
          <title>{`${row.category}: ${money(row.amount)} (${pct(row.amount, total)})`}</title>
        </path>
      ))}
      {arcs.filter(({ share }) => share >= 0.03).map(({ row, pctText, lx, ly }) => (
        <text key={`l-${row.category}`} x={lx} y={ly} textAnchor="middle" dominantBaseline="central"
          fontSize="12" fontWeight="700" fill="var(--text-dark)">
          {row.category}
          <tspan fontSize="11" fontWeight="400" fill="var(--text-mid)">{pctText}</tspan>
        </text>
      ))}
      <text x={cx} y={cy - 1} textAnchor="middle" fontSize="19" fontWeight="700" fill="var(--heading)">
        {rows.filter((r) => r.amount > 0).length}
      </text>
      <text x={cx} y={cy + 15} textAnchor="middle" fontSize="11" fill="var(--text-mid)">{rows.filter((r) => r.amount > 0).length === 1 ? 'category' : 'categories'}</text>
    </svg>
  );
}


// Monthly milestone attainment as a ring: how much of what we committed to this month is done,
// with a tick showing how far through the month we are. Read together they answer "are we on
// track" rather than just "what's the number" -- 40% on the 5th is fine, 40% on the 28th is not.
// The countable figures on the This Month card double as filters into the grid.
function MonthLink({ active, onClick, children }) {
  return (
    <button
      type="button" onClick={onClick} aria-pressed={active}
      title="Show these in the grid below"
      style={{
        border: 'none', background: 'none', padding: 0, font: 'inherit', cursor: 'pointer',
        color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted',
        textUnderlineOffset: 3, fontWeight: active ? 700 : undefined,
      }}
    >
      {children}
    </button>
  );
}

function AttainmentGauge({ attainment }) {
  const { pct, dayProgress, plannedCount, hitCount } = attainment;
  const size = 150;
  const r = 58;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const shown = pct ?? 0;
  // Behind only counts once the month is far enough along to judge -- see the note above.
  const behind = pct != null && pct + 10 < dayProgress;
  const ringColor = pct == null ? 'var(--border)' : behind ? 'var(--accent-orange)' : 'var(--green)';
  const tickAngle = (dayProgress / 100) * 2 * Math.PI - Math.PI / 2;
  const tick = (radius) => [c + radius * Math.cos(tickAngle), c + radius * Math.sin(tickAngle)];
  const [tx1, ty1] = tick(r - 11);
  const [tx2, ty2] = tick(r + 11);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img"
      aria-label={pct == null ? 'Nothing due this month' : `${pct}% of this month's milestones hit, ${dayProgress}% through the month`}>
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--light-bg)" strokeWidth="14" />
      {pct != null && (
        <circle
          cx={c} cy={c} r={r} fill="none" stroke={ringColor} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={`${(shown / 100) * circumference} ${circumference}`}
          transform={`rotate(-90 ${c} ${c})`}
        />
      )}
      {/* Where we'd be if the month's work landed evenly -- the "should be here by now" mark. */}
      <line x1={tx1} y1={ty1} x2={tx2} y2={ty2} stroke="var(--text-dark)" strokeWidth="2" strokeLinecap="round">
        <title>{`${dayProgress}% through the month`}</title>
      </line>
      <text x={c} y={c - 2} textAnchor="middle" fontSize="30" fontWeight="700" fill={pct == null ? 'var(--text-mid)' : 'var(--heading)'}>
        {pct == null ? '—' : `${pct}%`}
      </text>
      <text x={c} y={c + 18} textAnchor="middle" fontSize="11" fill="var(--text-mid)">
        {pct == null ? 'none due' : `${hitCount} of ${plannedCount}`}
      </text>
    </svg>
  );
}

// Every summary panel exports the same three ways (Judy, 2026-09-16): PDF for a slide, HTML for an
// email, Excel for anyone who wants the numbers. The data is built from the same values the panel
// renders rather than scraped from the DOM, so an export can't drift from what's on screen.
function PanelExport({ label, sectionId, sheets, onEmail }) {
  const [open, setOpen] = useState(false);
  function run(kind) {
    setOpen(false);
    if (kind === 'pdf') return printSection(sectionId);
    if (kind === 'email') return onEmail(label, sheets());
    const wb = XLSX.utils.book_new();
    for (const sheet of sheets()) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet.rows), sheet.name.slice(0, 31));
    }
    return XLSX.writeFile(wb, `${safeFileSegment(label)}.xlsx`);
  }
  return (
    <span style={{ position: 'relative', marginLeft: 'auto', fontWeight: 400 }}>
      <button
        type="button" className="export-btn" onClick={() => setOpen((v) => !v)}
        title={`Export "${label}" as PDF, email or Excel`}
        style={{ height: 24 }}
      >
        Export ▾
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 11, marginTop: 4, padding: 5,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-md)', minWidth: 130 }}>
            {[['pdf', 'PDF'], ['email', 'Email (HTML)'], ['excel', 'Excel']].map(([kind, text]) => (
              <button
                key={kind} type="button" onClick={() => run(kind)}
                style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12, padding: '5px 8px',
                  border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-dark)' }}
              >
                {text}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

export default function MilestoneTracker() {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [trackFilter, setTrackFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(DEFAULT_BILLING_CATEGORY_FILTER);
  // Opens on open work only. Not pre-filtered to a year: that hid slipped prior-year and undated
  // milestones, the ones billing most needs to see.
  const [yearFilter, setYearFilter] = useRememberedPeriod('milestoneTracker.year', '');
  const [targetFrom, setTargetFrom] = useState('');
  const [targetTo, setTargetTo] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [view, setView] = useState('list');
  const [grain, setGrain] = useRememberedPeriod('milestoneTracker.grain', 'quarter');
  const [hidden, setHidden] = useState(() => new Set(DEFAULT_HIDDEN));
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [colWidths, setColWidths] = useColumnWidths();
  const [copied, setCopied] = useState('');
  const [sort, toggleSort] = useGridSort('targetDate');
  const [columnOrder, setColumnOrder] = useState(ALL_COLUMN_KEYS);
  const [draggedKey, setDraggedKey] = useState(null);

  useEffect(() => {
    apiFetch('/milestones?includeProject=true')
      // Expanded once, here, so every total on the page counts the same rows (ADR 0046).
      .then((rows) => setMilestones(expandRecurring(expandDrawdowns(rows))))
      .catch((err) => setError('Could not load milestones — ' + err.message))
      .finally(() => setLoading(false));
  }, []);

  const today = todayYmd();
  const columnDefs = milestoneColumns(today);

  const projects = useMemo(() => {
    const byId = new Map();
    for (const m of milestones) if (m.project) byId.set(m.project.id, m.project);
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [milestones]);

  const years = useMemo(() => {
    const set = new Set();
    for (const m of milestones) {
      const y = yearOf(periodDateOf(m));
      if (y) set.add(y);
    }
    return [...set].sort();
  }, [milestones]);

  const selectedProject = projectFilter ? projects.find((p) => p.id === projectFilter) : null;

  // Scope for the cards, YoY summary and charts: the project/status/track filters, but NOT the year
  // filter or a card click. The year filter is what the YoY table breaks down, so applying it there
  // would leave a one-row summary; the cards stay comparable while you click between them.
  const scopeMilestones = useMemo(() => milestones.filter((m) => {
    if (projectFilter && m.projectId !== projectFilter) return false;
    if (statusFilter && m.status !== statusFilter) return false;
    if (trackFilter && trackOf(m) !== trackFilter) return false;
    if (!categoryFilter.includes(categoryOf(m))) return false;
    return true;
  }), [milestones, projectFilter, statusFilter, trackFilter, categoryFilter]);

  // The default filter hides License/Hosting/MA, so say how much is out of view rather than letting
  // the totals quietly disagree with what someone remembers the contract being worth.
  const hiddenByCategory = useMemo(() => milestones.filter((m) => {
    if (projectFilter && m.projectId !== projectFilter) return false;
    if (statusFilter && m.status !== statusFilter) return false;
    if (trackFilter && trackOf(m) !== trackFilter) return false;
    return !categoryFilter.includes(categoryOf(m));
  }), [milestones, projectFilter, statusFilter, trackFilter, categoryFilter]);

  // What the cards total: everything the grid shows except a card click and Show completed (cards always
  // count completed, Judy 2026-11-02), so the cards add up
  // to the list underneath them (Judy, 2026-10-05 -- picking a month used to narrow the grid while
  // the cards stayed on the whole portfolio, which just reads as wrong). The card click is the one
  // filter they ignore, so the numbers stay comparable while you click between cards.
  const cardScope = useMemo(() => scopeMilestones.filter((m) => {
    if (yearFilter && yearOf(periodDateOf(m)) !== yearFilter) return false;
    // An arbitrary target-date window, which Year and the Mo/Qtr grain can't express. Compared as
    // ISO strings, same as everywhere else here (ADR 0009).
    const target = ymd(m.targetDate);
    if (targetFrom && (!target || target < targetFrom)) return false;
    if (targetTo && (!target || target > targetTo)) return false;
    return true;
  }), [scopeMilestones, yearFilter, targetFrom, targetTo]);

  // The grid is the card scope plus the clicked card. Derived from it rather than re-filtering, so
  // the two cannot drift apart.
  const filtered = useMemo(
    () => cardScope.filter((m) => (showDone || !isDone(m)) && matchesCardFilter(m, activeFilter, today)),
    [cardScope, activeFilter, today, showDone],
  );

  const cardTotals = useMemo(() => {
    const t = { overdue: [0, 0], due30: [0, 0], due60: [0, 0], due90: [0, 0], complete: [0, 0], later: [0, 0], unscheduled: [0, 0] };
    // scope and remaining aren't buckets -- scope is the denominator every percentage is of, and
    // remaining is simply everything not complete, so it spans several buckets on purpose.
    t.scope = [0, 0];
    t.remaining = [0, 0];
    let scope = 0;
    for (const m of cardScope) {
      const amt = amountOf(m);
      scope += amt;
      const bucket = milestoneBucket(m, today);
      if (t[bucket]) { t[bucket][0] += 1; t[bucket][1] += amt; }
      if (!isDone(m)) { t.remaining[0] += 1; t.remaining[1] += amt; }
    }
    t.scope = [cardScope.length, scope];
    return { t, scope };
  }, [cardScope, today]);

  // YoY and the burn-down deliberately stay on the wider scope: they ARE the breakdown over time,
  // so narrowing them to the selected year or month would leave a single column.
  const yoy = useMemo(() => yearTotals(scopeMilestones), [scopeMilestones]);
  // Contract facts (TCV, committed/contingent, MA) follow the project and Year picks, so
  // picking 2026 shows 2026's contract value (Judy, 2026-11-02). Every other filter stays off: the
  // card breaks value down BY category, so the billing filter would just hide part of it.
  const contractScope = useMemo(() => milestones.filter((m) => (!projectFilter || m.projectId === projectFilter)
    && (!yearFilter || yearOf(periodDateOf(m)) === yearFilter)), [milestones, projectFilter, yearFilter]);
  const tcv = useMemo(
    () => categoryTotals(
      contractScope,
      [...BILLING_CATEGORIES],
    ),
    [contractScope],
  );
  // Earliest dated milestone anywhere in the contract, whatever its category -- the one date worth
  // stating next to TCV now that the per-category starts are gone.
  const tcvFirstYear = useMemo(() => {
    const dates = tcv.rows.map((r) => r.startDate).filter(Boolean).sort();
    return dates[0] ? dates[0].slice(0, 4) : null;
  }, [tcv]);
  // The number people mean by "services" -- delivery work, as against license/hosting/maintenance.
  // Same project-only scope as TCV: attainment is a delivery fact, not a view, and letting a
  // status or FCST/ACT filter move it would make the exec number meaningless (filter to ACT and
  // it reads 100%).
  const attainment = useMemo(
    () => monthAttainment(projectFilter ? milestones.filter((m) => m.projectId === projectFilter) : milestones, today),
    [milestones, projectFilter, today],
  );

  // Committed vs contingent, on the same project-only scope as TCV -- contract facts, not a view.
  const certainty = useMemo(
    () => contractCertainty(contractScope),
    [contractScope],
  );
  // MA follows All services, then the rest in their usual order (Judy, 2026-09-16).
  const orderedNonServiceRows = useMemo(() => {
    const rest = tcv.rows.filter((r) => !SERVICE_CATEGORIES.includes(r.category));
    return [...rest.filter((r) => r.category === 'MA'), ...rest.filter((r) => r.category !== 'MA')];
  }, [tcv]);
  const services = useMemo(() => {
    const rows = tcv.rows.filter((r) => SERVICE_CATEGORIES.includes(r.category));
    return {
      rows,
      amount: rows.reduce((sum, r) => sum + r.amount, 0),
      count: rows.reduce((sum, r) => sum + r.count, 0),
    };
  }, [tcv]);
  const ma = useMemo(
    () => maSummary(contractScope),
    [contractScope],
  );
  // Earliest year with dated work in it -- 'Unscheduled' sorts last in yearTotals, so it can only be
  // picked up here when nothing is dated at all, which is why it's filtered out rather than sliced.
  const firstYear = useMemo(() => yoy.find((r) => r.year !== 'Unscheduled')?.year || null, [yoy]);
  const burndown = useMemo(() => burndownSeries(scopeMilestones, grain, today), [scopeMilestones, grain, today]);
  const pivoted = useMemo(() => pivot(filtered, grain), [filtered, grain]);

  // The Project column is redundant once a single project is picked -- its name moves to the header
  // instead (Judy, 2026-09-16).
  const visibleColumns = columnOrder
    .map((key) => columnDefs.find((c) => c.key === key))
    .filter((c) => c && !hidden.has(c.key) && !(PROJECT_COLS.includes(c.key) && projectFilter));

  // A recurring line is one row in the grid but its months in every total above (see expandRecurring).
  const sorted = sortRows(collapseRecurring(filtered), sort, (row, key) => columnDefs.find((c) => c.key === key).value(row));
  const filteredTotal = filtered.reduce((sum, m) => sum + amountOf(m), 0);

  function reorderColumns(targetKey) {
    if (!draggedKey || draggedKey === targetKey) { setDraggedKey(null); return; }
    setColumnOrder((prev) => {
      const next = prev.filter((k) => k !== draggedKey);
      next.splice(next.indexOf(targetKey), 0, draggedKey);
      return next;
    });
    setDraggedKey(null);
  }

  // Every filter in one place, so the count on the button and what the button clears can't drift
  // apart. The project is deliberately NOT in here (Judy, 2026-09-16): clearing filters while you're
  // looking at one project should leave you on that project -- the dropdown is how you change it.
  // "Cleared" for categories means every category, not the PS + R&D the page opens on (Judy,
  // 2026-09-17). PS + R&D is the default *view*, but it is still a filter -- it hides License,
  // Hosting and MA -- so it counts as one until you clear it.
  const ALL_CATEGORIES = [...BILLING_CATEGORIES, UNCATEGORIZED];
  const categoryIsAll = ALL_CATEGORIES.every((c) => categoryFilter.includes(c));
  const activeFilterCount = [
    statusFilter, trackFilter, yearFilter, activeFilter, targetFrom, targetTo,
    categoryIsAll ? '' : 'category',
  ].filter(Boolean).length;

  function clearFilters() {
    setStatusFilter('');
    setTrackFilter('');
    setYearFilter('');
    setTargetFrom('');
    setTargetTo('');
    setActiveFilter(null);
    setCategoryFilter(ALL_CATEGORIES);
  }

  function showThisMonth(key) {
    const turningOff = activeFilter === key;
    setActiveFilter(turningOff ? null : key);
    if (turningOff) return;
    setCategoryFilter(ALL_CATEGORIES);
    setStatusFilter('');
    setTrackFilter('');
    setYearFilter('');
    setShowDone(true);
  }

  function setColumnWidth(key, width) {
    setColWidths((prev) => ({ ...prev, [key]: width }));
  }

  function toggleColumn(key) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // Export carries the full ISO dates and raw numbers, not the month/year and $K the grid shows --
  // the screen is for scanning, the file is for working in.
  function exportXlsx() {
    const rows = sorted.map((m) => {
      const out = {};
      for (const col of visibleColumns) out[col.label] = col.excel(m);
      return out;
    });
    const totalRow = {};
    for (const col of visibleColumns) {
      totalRow[col.label] = col.key === 'amount' ? filteredTotal : '';
    }
    if (visibleColumns[0]) totalRow[visibleColumns[0].label] = 'TOTAL';
    rows.push(totalRow);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Milestones');
    XLSX.writeFile(wb, `Milestone Tracker ${safeFileSegment(selectedProject?.name || 'All projects')}.xlsx`);
  }

  // One builder per panel. Plain rows of label/value objects, so the same shape feeds both the
  // Excel sheet and the HTML table without either knowing about the other.
  const sheetsFor = {
    cards: () => [{
      name: 'Summary',
      isMoney: (col) => col === 'Amount',
      rows: CARDS.map(({ key, label }) => ({
        Measure: label,
        Amount: t[key][1],
        '% of total': key === 'scope' ? '' : pct(t[key][1], scope),
        Milestones: t[key][0],
      })),
    }],
    tcv: () => [{
      name: 'Contract value',
      isMoney: (col) => col === 'Amount',
      rows: [
        { Category: 'TCV (all categories)', Amount: tcv.total, '% of TCV': '100%', Milestones: '' },
        ...tcv.rows.map((r) => ({ Category: r.category, Amount: r.amount, '% of TCV': pct(r.amount, tcv.total), Milestones: r.count })),
        { Category: 'MA target start', Amount: ma.amount, '% of TCV': pct(ma.amount, tcv.total), Milestones: ma.count, Date: ma.startDate || '' },
      ],
    }],
    progress: () => [{
      name: 'Billing progress',
      isMoney: (col) => ['FCST', 'ACT', 'Total'].includes(col),
      rows: [
        ...yoy.map((r) => ({ Year: r.year, FCST: r.fcst, ACT: r.act, Total: r.fcst + r.act })),
        { Year: 'All years', FCST: yoy.reduce((n, r) => n + r.fcst, 0), ACT: yoy.reduce((n, r) => n + r.act, 0), Total: scope },
        { Year: 'POC', FCST: '', ACT: pct(t.complete[1], scope), Total: '' },
      ],
    }],
    burndown: () => [{
      name: 'Remaining over time',
      isMoney: (col) => col !== 'Period',
      rows: burndown.points.map((pt) => ({
        Period: periodLabelOf(pt.key, grain),
        'Billed this period': pt.delivered,
        'Scheduled this period': pt.due,
        'Planned remaining': pt.planned,
        'Actual remaining': pt.actual == null ? '' : pt.actual,
      })),
    }],
  };
  sheetsFor.attainment = () => [{
    name: 'Monthly attainment',
    // Label/value shaped, so the money rows are identified by their label, not their column.
    isMoney: (col, row) => col === 'Value' && /($)/.test(String(row.Measure || '')),
    rows: [
      { Measure: 'Month', Value: monthYearLabel(`${attainment.month}-01`) },
      { Measure: 'Planned (milestones)', Value: attainment.plannedCount },
      { Measure: 'Hit', Value: attainment.hitCount },
      { Measure: 'Still open', Value: attainment.missedCount },
      { Measure: 'Achieved (%)', Value: attainment.pct == null ? 'n/a' : `${attainment.pct}%` },
      { Measure: 'Planned ($)', Value: attainment.plannedAmount },
      { Measure: 'Hit ($)', Value: attainment.hitAmount },
      { Measure: 'Achieved by value (%)', Value: attainment.pctAmount == null ? 'n/a' : `${attainment.pctAmount}%` },
      { Measure: 'Delivered but targeted elsewhere', Value: attainment.unplannedCount },
      { Measure: 'Through the month (%)', Value: `${attainment.dayProgress}%` },
      ...attainment.missed.map((m) => ({ Measure: 'Still open', Value: m.title })),
    ],
  }];
  sheetsFor.all = () => [
    ...sheetsFor.cards(), ...sheetsFor.tcv(), ...sheetsFor.attainment(), ...sheetsFor.progress(), ...sheetsFor.burndown(),
  ];

  // Generic HTML-table email for a panel, same clipboard mechanics and escaping as the grid's.
  async function emailSheets(label, sheets) {
    const heading = `${selectedProject?.name || 'All projects'} — ${label}, ${today}`;
    const tables = sheets.map((sheet) => {
      const cols = [...new Set(sheet.rows.flatMap((r) => Object.keys(r)))];
      const head = cols.map((col) => `<th style="background:#044060;color:#fff;padding:4px 8px;text-align:left;font-size:12px">${escapeHtml(col)}</th>`).join('');
      const isMoney = sheet.isMoney || ((col) => /amount|fcst|act|total|remaining/i.test(col));
      const body = sheet.rows.map((row) => `<tr>${cols.map((col) => {
        const raw = row[col];
        const text = typeof raw === 'number' && isMoney(col, row) ? money(raw) : raw;
        return `<td style="padding:3px 8px;font-size:12px;border-bottom:1px solid #dde1e6">${escapeHtml(text)}</td>`;
      }).join('')}</tr>`).join('');
      return `<p style="font-family:Segoe UI,Arial,sans-serif;font-weight:600;margin:12px 0 4px">${escapeHtml(sheet.name)}</p>`
        + `<table style="border-collapse:collapse;font-family:Segoe UI,Arial,sans-serif"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    }).join('');
    await copyHtml(`<p style="font-family:Segoe UI,Arial,sans-serif"><strong>${escapeHtml(heading)}</strong></p>${tables}`);
  }

  // Inline styles rather than classes, because it's pasted into Outlook -- a stylesheet doesn't
  // survive the trip. Clipboard rather than a mailto: link, whose URL length caps out well under a
  // table this size.
  async function copyForEmail() {
    const head = visibleColumns.map((c) => `<th style="background:#044060;color:#fff;padding:4px 8px;text-align:left;font-size:12px">${escapeHtml(c.label)}</th>`).join('');
    const body = sorted.map((m) => {
      const state = rowState(m, today);
      const bg = state === 'done' ? '#e6f4ec' : state === 'overdue' ? '#fbeae8' : state === 'dueSoon' ? '#fdf3e0' : '#ffffff';
      const cells = visibleColumns.map((c) => {
        const raw = c.excel(m);
        const text = c.key === 'amount' ? money(raw) : (String(raw ?? '').match(/^\d{4}-\d{2}-\d{2}$/) ? monthYearLabel(raw) : raw);
        return `<td style="padding:3px 8px;font-size:12px;border-bottom:1px solid #dde1e6">${escapeHtml(text)}</td>`;
      }).join('');
      return `<tr style="background:${bg}">${cells}</tr>`;
    }).join('');
    const totalRow = `<tr><td colspan="${visibleColumns.length}" style="padding:4px 8px;font-size:12px;font-weight:700;border-top:2px solid #044060">TOTAL ${money(filteredTotal)} — ${sorted.length} milestones</td></tr>`;
    const html = `<p style="font-family:Segoe UI,Arial,sans-serif"><strong>${escapeHtml(selectedProject?.name || 'All projects')}</strong> — Milestone Tracker, ${today}</p>`
      + `<table style="border-collapse:collapse;font-family:Segoe UI,Arial,sans-serif">`
      + `<thead><tr>${head}</tr></thead><tbody>${body}${totalRow}</tbody></table>`;
    await copyHtml(html);
  }

  // text/html so Outlook pastes the formatting; the plain-text alternative keeps it pasteable
  // somewhere that can't take HTML.
  async function copyHtml(html) {
    try {
      if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([html], { type: 'text/plain' }),
        })]);
      } else {
        await navigator.clipboard.writeText(html);
      }
      setCopied('Copied — paste into Outlook');
    } catch {
      setCopied('Copy failed — your browser blocked clipboard access');
    }
    setTimeout(() => setCopied(''), 4000);
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>;
  }

  const { t, scope } = cardTotals;
  const cellPad = '2px 8px';

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services</div>
        {selectedProject ? (
          <>
            <h1 className="page-title" style={{ fontSize: 30 }}>{selectedProject.name}</h1>
            {/* Just "Milestone Tracker" -- the county is already in the project name above it. */}
            <p className="page-subtitle">Milestone Tracker</p>
          </>
        ) : (
          <>
            <h1 className="page-title">Milestone Tracker</h1>
            <p className="page-subtitle">What's due, what's overdue, and what it's worth, across every project.</p>
          </>
        )}
      </div>

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 16, background: 'rgba(239,65,54,0.08)',
          border: '1px solid rgba(239,65,54,0.3)', fontSize: 13, color: 'var(--danger-text)' }}>
          ⚠ {error}
        </div>
      )}

      {/* Filters lead the page, as on the Revenue Tracker: everything below follows them (Judy, 2026-11-02). */}
      <div className="card" style={{ marginBottom: 12, padding: '10px 12px' }}>
        <div className="mt-toolbar">
          <select className="mt-ctl" style={{ maxWidth: 240 }} value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
            <option value="">All projects</option>
            {projects.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
          </select>
          <select className="mt-ctl" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="mt-ctl" value={trackFilter} onChange={(e) => setTrackFilter(e.target.value)}>
            <option value="">FCST + ACT</option>
            <option value="FCST">FCST only</option>
            <option value="ACT">ACT only</option>
          </select>
          <BillingCategoryFilter selected={categoryFilter} onChange={setCategoryFilter} />
          <select className="mt-ctl" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {MILESTONE_STATUSES.map((st) => <option key={st}>{st}</option>)}
          </select>
          <label className="mt-check" title="Target date on or after">
            <input
              type="date" className="mt-ctl" style={{ width: 132 }} value={targetFrom}
              onChange={(e) => setTargetFrom(e.target.value)} aria-label="Target date from"
            />
          </label>
          <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>–</span>
          <label className="mt-check" title="Target date on or before">
            <input
              type="date" className="mt-ctl" style={{ width: 132 }} value={targetTo}
              onChange={(e) => setTargetTo(e.target.value)} aria-label="Target date to"
            />
          </label>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="mt-seg">
              {[{ k: 'list', l: 'List' }, { k: 'calendar', l: 'Calendar' }].map((o) => (
                <button key={o.k} type="button" onClick={() => setView(o.k)} aria-pressed={view === o.k}>{o.l}</button>
              ))}
            </div>
            <div className="mt-seg" title="Period grain for the calendar view and the burn-down">
              {[{ k: 'month', l: 'Mo' }, { k: 'quarter', l: 'Qtr' }].map((o) => (
                <button key={o.k} type="button" onClick={() => setGrain(o.k)} aria-pressed={grain === o.k}>{o.l}</button>
              ))}
            </div>
            <div style={{ position: 'relative' }}>
              <button type="button" className="mt-ctl" onClick={() => setColumnsOpen((v) => !v)}>Columns ▾</button>
              {columnsOpen && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setColumnsOpen(false)} />
                  <div className="mt-pop">
                    {columnDefs.map((col) => (
                      <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0', cursor: 'pointer' }}>
                        <input
                          type="checkbox" checked={!hidden.has(col.key)} onChange={() => toggleColumn(col.key)}
                          disabled={PROJECT_COLS.includes(col.key) && !!projectFilter}
                        />
                        {col.label}
                      </label>
                    ))}
                    <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
                      <button
                        type="button" className="mt-ctl" style={{ width: '100%', justifyContent: 'center' }}
                        disabled={Object.keys(colWidths).length === 0}
                        onClick={() => setColWidths({})}
                      >
                        Reset column widths
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              className={`mt-ctl${activeFilterCount ? ' is-primary' : ''}`}
              disabled={!activeFilterCount}
              onClick={clearFilters}
              title={projectFilter
                ? 'Clears year, category, status and FCST/ACT — categories go to All. Leaves the project selected; use the project dropdown to change that.'
                : 'Clears every filter, including categories, which go to All rather than back to the PS + R&D + Uncategorized default.'}
            >
              Clear filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
            </button>
          </div>
        </div>
      </div>

      {/* Everything above the grid is one printable/exportable unit as well as four separate ones
          (Judy, 2026-09-16) -- the whole block is the presentation, the panels are the slides. */}
      <div id="mt-summary">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-mid)' }}>Summary</span>
        <PanelExport label="Milestone summary" sectionId="mt-summary" sheets={sheetsFor.all} onEmail={emailSheets} />
      </div>
      <div id="mt-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 12, marginBottom: 14 }}>
        {CARDS.map(({ key, label, accent, value, filterable }) => {
          const [count, amount] = t[key];
          const clickable = filterable !== false;
          const active = activeFilter === key;
          const color = value === 'bad' ? 'var(--danger-text)' : value === 'good' ? 'var(--green)' : 'var(--heading)';
          const toggle = () => clickable && setActiveFilter((prev) => (prev === key ? null : key));
          return (
            <div
              key={key}
              // Accent and active state come from .mt-card in App.css -- see the note there.
              className={`card mt-card accent-${accent}${active ? ' is-active' : ''}`}
              onClick={clickable ? toggle : undefined}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } } : undefined}
              title={clickable ? 'Click to filter the grid below' : undefined}
              aria-pressed={clickable ? active : undefined}
              style={{
                padding: '14px 16px', cursor: clickable ? 'pointer' : 'default',
                background: active ? 'var(--hover-tint)' : undefined,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-mid)', marginBottom: 4, whiteSpace: 'nowrap' }}>{label}</div>
              {/* $ at the same weight as the count (Judy, 2026-09-16) -- the money is the point. */}
              <div style={{ fontSize: 24, fontWeight: 700, color, letterSpacing: '-0.5px' }}>
                {money(amount)}
                {key !== 'scope' && <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-mid)', letterSpacing: 0 }}> ({pct(amount, scope)})</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 4 }}>
                {plural(count)}
                {/* Across the whole portfolio the contracted total spans years, and "total" reads as
                    "this year" unless it says otherwise (Judy, 2026-09-16). */}
                {key === 'scope' && !projectFilter && firstYear && ` · from ${firstYear}`}
                {key === 'scope' && Math.round(amount) !== Math.round(tcv.total) && (
                  <span style={{ color: 'var(--warn-text)' }}>
                    {' · '}{money(tcv.total - amount)} excluded by your filters
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Contract value and its category split, in a card of its own rather than tucked under the
          progress bar -- it answers a different question from the filtered cards above. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 3fr) minmax(250px, 1fr)', gap: 12, marginBottom: 12, alignItems: 'stretch' }}>
      <div id="mt-tcv" className="card" style={{ padding: '12px 16px', borderLeft: '4px solid var(--accent-slate)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <span className="card-title">Contract value</span>
          <PanelExport label="Contract value" sectionId="mt-tcv" sheets={sheetsFor.tcv} onEmail={emailSheets} />
        </div>
        {/* Three bands across the card (Judy, 2026-09-16): the headline number, the donut next to
            it rather than at the far edge, and the category chips out to the right using the space
            that was empty. TCV follows only the project and Year picks (contractScope). */}
        {/* gap/maxWidth/flex-basis below are tuned together, measured not guessed: the three bands
            have to total under the card's inner width or the cards wrap to their own line and the
            card gets taller. At 365px the PS + R&D split stays on one line, which is what makes the
            card SHORTER than the old narrower layout despite the bigger type (Judy, 2026-09-17). */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '0 0 auto', maxWidth: 220 }}>
            {/* No headline $ here: Total contracted (the first card) already states it (Judy, 2026-11-02). */}
            <div style={{ fontSize: 12, color: 'var(--text-mid)', fontWeight: 600 }}>
              {yearFilter ? `in ${yearFilter}` : tcvFirstYear ? `from ${tcvFirstYear}` : 'no dated milestones'}
            </div>
            {/* Contingent money bills only if the customer uses or authorizes it, so it's stated
                beside the total rather than folded into it (Judy, 2026-09-16). */}
            <div style={{ fontSize: 12, marginTop: 6 }}>
              <span style={{ fontWeight: 700 }}>{money(certainty.committed)}</span>
              <span style={{ color: 'var(--text-mid)' }}> committed · </span>
              <span style={{ fontWeight: 700, color: certainty.contingent > 0 ? 'var(--warn-text)' : 'var(--text-mid)' }}>
                {money(certainty.contingent)}
              </span>
              <span style={{ color: 'var(--text-mid)' }}>
                {certainty.contingent > 0
                  ? ` contingent (${certainty.rows.map((r) => r.valueType).join(', ')})`
                  : ' contingent — nothing marked Draw-down or Optional yet'}
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 2 }}>
              contract value — all categories{projectFilter ? '' : ', all projects'}{yearFilter ? `, billing in ${yearFilter}` : ''}; ignores the other filters below
            </div>
          </div>

          <div style={{ flex: '0 0 auto' }}>
            <CategoryPie rows={tcv.rows} total={tcv.total} />
          </div>

          <div style={{ flex: '1 1 365px', display: 'flex', flexWrap: 'wrap', gap: 8, alignContent: 'center' }}>
            {services.rows.length > 0 && (
              <div
                title={`PS + R&D — ${plural(services.count)}`}
                style={{
                  // Same card shape as MA and the rest -- the thick left border carries the color
                  // (Judy, 2026-11-02); teal is the one accent no category claims.
                  flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                  border: '1px solid var(--border)', borderLeft: '5px solid var(--teal)',
                  borderRadius: 'var(--radius)', padding: '8px 14px', background: 'var(--surface)',
                }}
              >
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-dark)' }}>All services</div>
                  {/* When services are the whole contract, its $ would just repeat Total contracted. */}
                  {Math.round(services.amount) === Math.round(tcv.total) ? (
                    <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>100% of contract value</div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 22, fontWeight: 700 }}>{money(services.amount)}</span>
                      <span style={{ fontSize: 13, color: 'var(--text-mid)' }}>({pct(services.amount, tcv.total)})</span>
                    </div>
                  )}
                </div>
                {/* The PS / R&D split, out to the right at nearly headline size. */}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {services.rows.map((row) => (
                    <div key={row.category}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 9, height: 9, borderRadius: 2, background: CATEGORY_COLOR[row.category] || CATEGORY_FALLBACK, display: 'inline-block' }} />
                        {row.category}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                        <span style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-dark)' }}>{money(row.amount)}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>({pct(row.amount, tcv.total)})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {orderedNonServiceRows.map((row) => (
              <div
                key={row.category}
                title={`${plural(row.count)} — ${money(row.amount)}`}
                style={{
                  // MA is the other card that gets referred to, so it takes more width and a bigger
                  // label than License/Hosting -- wider, not taller.
                  flex: row.category === 'MA' ? '2 1 250px' : '1 1 120px',
                  border: '1px solid var(--border)',
                  borderLeft: `5px solid ${CATEGORY_COLOR[row.category] || CATEGORY_FALLBACK}`,
                  borderRadius: 'var(--radius)', padding: row.category === 'MA' ? '8px 14px' : '7px 11px',
                  background: 'var(--surface)',
                }}
              >
                <div style={{
                  fontSize: row.category === 'MA' ? 15 : 11,
                  fontWeight: row.category === 'MA' ? 700 : 600,
                  color: row.category === 'MA' ? 'var(--text-dark)' : 'var(--text-mid)',
                }}>{row.category}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: row.category === 'MA' ? 22 : 16, fontWeight: 700 }}>{money(row.amount)}</span>
                  <span style={{ fontSize: row.category === 'MA' ? 13 : 12, color: 'var(--text-mid)' }}>({pct(row.amount, tcv.total)})</span>
                </div>
                {row.category === 'MA' && (
                  <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 2 }}>
                    {row.startDate ? `target start ${monthYearLabel(row.startDate)}` : 'no target date'}
                  </div>
                )}

              </div>
            ))}
          </div>
        </div>
      </div>

      <div id="mt-attainment" className="card" style={{ padding: '12px 16px', borderLeft: '4px solid var(--accent-blue)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <span className="card-title">This month</span>
          <PanelExport label="Monthly attainment" sectionId="mt-attainment" sheets={sheetsFor.attainment} onEmail={emailSheets} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 6 }}>
          {monthYearLabel(`${attainment.month}-01`)} — milestones hit against plan
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => showThisMonth('month')}
            aria-pressed={activeFilter === 'month'}
            title="Show this month's milestones in the grid below"
            style={{
              border: activeFilter === 'month' ? '2px solid var(--primary-dark)' : '2px solid transparent',
              borderRadius: '50%', background: 'none', padding: 0, cursor: 'pointer', lineHeight: 0,
            }}
          >
            <AttainmentGauge attainment={attainment} />
          </button>
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>
            <div>
              <MonthLink active={activeFilter === 'monthHit'} onClick={() => showThisMonth('monthHit')}>
                <strong style={{ fontSize: 15 }}>{attainment.hitCount}</strong> hit
              </MonthLink>
            </div>
            <div>
              <MonthLink active={activeFilter === 'monthOpen'} onClick={() => showThisMonth('monthOpen')}>
                <strong style={{ fontSize: 15 }}>{attainment.missedCount}</strong> still open
              </MonthLink>
            </div>
            <div style={{ color: 'var(--text-mid)' }}>{money(attainment.hitAmount)} of {money(attainment.plannedAmount)}</div>
            {attainment.pctAmount != null && (
              <div style={{ color: 'var(--text-mid)' }}>{attainment.pctAmount}% by value</div>
            )}
            {attainment.unplannedCount > 0 && (
              <div style={{ color: 'var(--text-mid)' }} title="Delivered this month but targeted at another month — real delivery, but not attainment against this month's plan">
                +{attainment.unplannedCount} unplanned
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      <div id="mt-charts" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div id="mt-progress" className="card" style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <span className="card-title">Billing progress</span>
            <PanelExport label="Billing progress" sectionId="mt-progress" sheets={sheetsFor.progress} onEmail={emailSheets} />
          </div>
          <ProgressBar complete={t.complete[1]} scope={scope} />
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 4 }}>By year, all years: follows every filter except Year and target dates.</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: 'var(--text-mid)', textAlign: 'right' }}>
                    <th style={{ textAlign: 'left', padding: '2px 6px', fontWeight: 600 }} />
                    {yoy.map((col) => (
                      <th key={col.year} style={{ padding: '2px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}>{col.year}</th>
                    ))}
                    <th style={{ padding: '2px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}>All years</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'FCST', pick: (c) => c.fcst, color: undefined },
                    { label: 'ACT', pick: (c) => c.act, color: 'var(--green)' },
                    { label: 'Total', pick: (c) => c.fcst + c.act, color: undefined, bold: true },
                  ].map(({ label, pick, color, bold }) => (
                    <tr key={label} style={{ borderTop: label === 'Total' ? '2px solid var(--border)' : '1px solid var(--border)', textAlign: 'right', fontWeight: bold ? 700 : undefined }}>
                      <td style={{ textAlign: 'left', padding: '2px 6px', fontWeight: 600 }}>{label}</td>
                      {yoy.map((col) => (
                        <td key={col.year} style={{ padding: '2px 6px', color, whiteSpace: 'nowrap' }}>{money(pick(col))}</td>
                      ))}
                      <td style={{ padding: '2px 6px', color, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {money(yoy.reduce((sum, c) => sum + pick(c), 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-mid)', marginTop: 6 }}>
              Year-over-year ignores the Year filter on purpose — it's the breakdown that filter narrows.
              {hiddenByCategory.length > 0 && (
                <> {plural(hiddenByCategory.length)} worth {money(hiddenByCategory.reduce((sum, m) => sum + amountOf(m), 0))} hidden by the billing filter.</>
              )}
            </div>

          </div>
        </div>
        <div id="mt-burndown" className="card" style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <span className="card-title">Remaining to bill over time</span>
            <PanelExport label="Remaining to bill over time" sectionId="mt-burndown" sheets={sheetsFor.burndown} onEmail={emailSheets} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 4 }}>Full timeline: follows every filter except Year and target dates.</div>
          <Burndown series={burndown} grain={grain} />
        </div>
      </div>

      </div>

      <div className="card" style={{ padding: 0 }}>
        {/* List exports sit on the list they copy from (Judy, 2026-11-02). */}
        <div className="mt-toolbar" style={{ padding: '8px 12px', justifyContent: 'flex-end', borderBottom: '1px solid var(--border)' }}>
          {/* Completed rows are a list choice only; the cards above always count them (Judy, 2026-11-02). */}
          <label className="mt-check" style={{ marginRight: 'auto' }}>
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
            Show completed
          </label>
          <button type="button" className="export-btn" onClick={exportXlsx}>Excel</button>
          <div style={{ position: 'relative' }}>
            <button type="button" className="export-btn" onClick={() => setPdfOpen((v) => !v)} title="Print or save as PDF">PDF ▾</button>
            {pdfOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setPdfOpen(false)} />
                <div className="mt-pop" style={{ minWidth: 170, padding: 5 }}>
                  {[
                    { label: 'Summary cards', id: 'mt-cards' },
                    { label: 'Charts', id: 'mt-charts' },
                    { label: 'Whole page', id: null },
                  ].map((o) => (
                    <button
                      key={o.label} type="button"
                      onClick={() => { setPdfOpen(false); printSection(o.id); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12, padding: '5px 8px',
                        border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-dark)' }}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button type="button" className="export-btn" onClick={copyForEmail}>Email</button>
        </div>
        {copied && (
          <div style={{ margin: '6px 12px', fontSize: 12, color: 'var(--text-mid)' }}>{copied}</div>
        )}
        {/* print-expand: the scroll box has to unclip for the PDF button, or printing stops at
            whatever happens to be scrolled into view. */}
        <div className="print-expand" style={{ overflow: 'auto', maxHeight: 620 }}>
          {view === 'list' ? (
            <table className="mt-grid" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--light-bg)', textAlign: 'left' }}>
                  {visibleColumns.map((col) => (
                    <MilestoneColumnHeader
                      key={col.key} col={col} sort={sort} onSort={toggleSort}
                      draggedKey={draggedKey} onDragStart={setDraggedKey} onDrop={reorderColumns}
                      width={colWidths[col.key]} onResize={setColumnWidth}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => (
                  <tr key={m.id} className={rowClass(rowState(m, today))}>
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          padding: cellPad, textAlign: col.numeric ? 'right' : 'left',
                          // Only a resized column clips -- everything else keeps sizing to its text.
                          ...(colWidths[col.key]
                            ? { width: colWidths[col.key], maxWidth: colWidths[col.key], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                            : null),
                        }}
                      >
                        {col.render(m)}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* maxWidth none: .mt-grid caps a data cell at 360px, which would clip this. */}
                {sorted.length === 0 && (
                  <tr><td colSpan={visibleColumns.length} style={{ padding: 16, textAlign: 'center', color: 'var(--text-mid)', maxWidth: 'none' }}>No milestones match these filters.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ position: 'sticky', bottom: 0, background: 'var(--light-bg)', borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                  {visibleColumns.map((col, i) => (
                    <td key={col.key} style={{ padding: cellPad, textAlign: col.numeric ? 'right' : 'left' }}>
                      {i === 0 ? `Total — ${sorted.length}` : col.key === 'amount' ? money(filteredTotal) : ''}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          ) : (
            <CalendarView pivoted={pivoted} grain={grain} today={today} showProject={!projectFilter} hidden={hidden} total={filteredTotal} />
          )}
        </div>
      </div>
    </div>
  );
}

// The pivot the Power BI report did: one row per milestone, one column per month or quarter, the
// amount in the cell it's forecast or was delivered in, and a column total on the bottom row.
function CalendarView({ pivoted, grain, today, showProject, hidden, total }) {
  const { periods, rows, totals, unscheduled } = pivoted;
  const currentPeriod = periodKeyOf(today, grain);
  if (periods.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>No dated milestones match these filters.</div>;
  }
  // Sticky leading columns, so who and what stay visible while the period columns scroll sideways.
  // `left` has to be a fixed number for each to butt up against the previous, hence explicit widths.
  // State/County/Project/PM only across projects (Judy, 2026-10-29), and the Columns menu applies.
  const fixedCols = [
    ...(showProject ? [
      { key: 'state', label: 'State', w: 44, render: (m) => m.project?.account?.state || '—' },
      { key: 'county', label: 'County', w: 96, render: (m) => countyLabel(m.project) || '—' },
      { key: 'project', label: 'Project', w: 170, render: (m) => shortProjectName(m.project) || '—' },
      { key: 'pm', label: 'PM', w: 120, render: (m) => m.project?.projectManager?.name || '—' },
    ].filter((c) => !hidden.has(c.key)) : []),
    { key: 'name', label: 'Milestone', w: showProject ? 220 : 260 },
    { key: 'bill', label: 'Category', w: 64 },
  ];
  const leadCols = fixedCols.filter((c) => c.render);
  let left = 0;
  const fixed = {};
  for (const c of fixedCols) {
    fixed[c.key] = { padding: '2px 6px', position: 'sticky', left, zIndex: 1, width: c.w, minWidth: c.w, maxWidth: c.w, textAlign: 'left' };
    left += c.w;
  }
  const nameCol = fixed.name;
  const billCol = fixed.bill;
  const headCell = { position: 'sticky', top: 0, background: 'var(--light-bg)', fontWeight: 600 };
  // Period columns are tight so a couple of years of months fit on screen (Judy, 2026-10-28), and the
  // table is width:auto so they shrink to "$12.35M" rather than sharing out spare width (2026-10-29).
  const periodCell = { padding: '2px 4px', fontSize: 11 };
  // Quarter boundaries in the month view -- a heavier rule every third column, so a year's worth of
  // columns has some rhythm to it instead of reading as one undifferentiated block. The period we're
  // in gets a red rule instead: "where are we now" is the first thing anyone looks for on a grid
  // this wide (Judy, 2026-09-17). Deliberately only the line and the header, not a red column tint,
  // which would collide with the overdue row state.
  const periodEdge = (key, i) => {
    if (key === currentPeriod) return '2px solid var(--red)';
    return grain === 'month' && i > 0 && Number(key.slice(5, 7)) % 3 === 1
      ? '2px solid var(--border)' : '1px solid var(--border)';
  };
  return (
    <table className="mt-grid" style={{ borderCollapse: 'collapse', width: 'auto', fontSize: 12 }}>
      <thead>
        <tr>
          {leadCols.map((c) => (
            <th key={c.key} style={{ ...fixed[c.key], ...headCell, zIndex: 4 }}>{c.label}</th>
          ))}
          <th style={{ ...nameCol, ...headCell, textAlign: 'left', zIndex: 4 }}>Milestone</th>
          <th style={{ ...billCol, ...headCell, zIndex: 4 }}>Category</th>
          {periods.map((key, i) => (
            <th
              key={key}
              style={{ ...periodCell, ...headCell, textAlign: 'right', whiteSpace: 'nowrap', zIndex: 2,
                borderLeft: periodEdge(key, i),
                background: key === currentPeriod ? 'var(--hover-tint)' : 'var(--light-bg)',
                color: key === currentPeriod ? 'var(--danger-text)' : undefined,
                fontWeight: key === currentPeriod ? 700 : 600 }}
              title={periodLabelOf(key, grain)}
            >
              {compactPeriodLabel(key, grain)}
              {key === currentPeriod && (
                <span style={{ display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--red)', letterSpacing: '0.5px' }}>
                  TODAY
                </span>
              )}
            </th>
          ))}
          {unscheduled > 0 && <th style={{ ...periodCell, ...headCell, textAlign: 'right', zIndex: 2, borderLeft: '2px solid var(--border)' }}>No date</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map(({ milestone: m, amount, cells }) => {
          // The sticky cells need their own opaque background or the scrolling cells show through
          // them; .mt-row.is-* already paints every td, so only the untinted case sets one.
          const state = rowState(m, today);
          const stickyBg = state ? undefined : 'var(--surface)';
          return (
            <tr key={m.id} className={rowClass(state)}>
              {leadCols.map((c) => {
                const text = c.render(m);
                return <td key={c.key} style={{ ...fixed[c.key], background: stickyBg, zIndex: 2 }} title={text}>{text}</td>;
              })}
              <td style={{ ...nameCol, background: stickyBg, zIndex: 2 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span className={`badge ${trackOf(m) === 'ACT' ? 'badge-green' : 'badge-blue'}`} style={{ fontSize: 9, flexShrink: 0 }}>{trackOf(m)}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.title}>
                    {m.title}
                  </span>
                </div>
              </td>
              <td style={{ ...billCol, background: stickyBg, zIndex: 2 }}>
                {m.billingCategory
                  ? <span className="badge badge-blue" style={{ fontSize: 9 }}>{m.billingCategory}</span>
                  : <span style={{ color: 'var(--text-mid)' }}>—</span>}
              </td>
              {periods.map((key, i) => (
                <td
                  key={key}
                  style={{ ...periodCell, textAlign: 'right', whiteSpace: 'nowrap', borderLeft: periodEdge(key, i),
                    fontVariantNumeric: 'tabular-nums',
                    background: key === currentPeriod && !state ? 'var(--hover-tint)' : undefined }}
                  title={cells[key] != null ? `${m.title} — ${money(cells[key])}` : undefined}
                >
                  {cells[key] != null ? moneyShort(cells[key]) : ''}
                </td>
              ))}
              {unscheduled > 0 && <td style={{ ...periodCell, textAlign: 'right', borderLeft: '2px solid var(--border)' }}>{Object.keys(cells).length ? '' : moneyShort(amount)}</td>}
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr style={{ position: 'sticky', bottom: 0, background: 'var(--light-bg)', borderTop: '2px solid var(--border)', fontWeight: 700 }}>
          {leadCols.map((c) => (
            <td key={c.key} style={{ ...fixed[c.key], background: 'var(--light-bg)', zIndex: 4 }} />
          ))}
          <td style={{ ...nameCol, background: 'var(--light-bg)', zIndex: 4 }}>Total — {money(total)}</td>
          <td style={{ ...billCol, background: 'var(--light-bg)', zIndex: 4 }} />
          {periods.map((key, i) => (
            <td key={key} style={{ ...periodCell, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', borderLeft: periodEdge(key, i) }} title={money(totals[key])}>
              {moneyShort(totals[key])}
            </td>
          ))}
          {unscheduled > 0 && <td style={{ ...periodCell, textAlign: 'right', borderLeft: '2px solid var(--border)' }}>{moneyShort(unscheduled)}</td>}
        </tr>
      </tfoot>
    </table>
  );
}
